import type { ComponentInternalInstance } from 'vue'
import { onActivated, onDeactivated, ref } from 'vue'
import { useHead } from '#imports'
import type { ActiveHeadEntry, UseHeadInput, UseHeadOptions } from '@unhead/vue'
import type { SchemaAugmentations } from '@unhead/schema'
import { resolveUnref, watchOnce } from '@vueuse/core'

const _isHydrated = ref<boolean>(false)
export const isHydrated = _isHydrated

export function onHydrated(cb: () => unknown) {
  watchOnce(isHydrated, () => cb(), { immediate: isHydrated.value })
}

export function useDeactivated() {
  const deactivated = ref(false)
  onActivated(() => (deactivated.value = false))
  onDeactivated(() => (deactivated.value = true))

  return deactivated
}
export function onReactivated(hook: () => void, target?: ComponentInternalInstance | null): void {
  const initial = ref(true)
  onActivated(() => {
    if (initial.value) return
    hook()
  }, target)
  onDeactivated(() => (initial.value = false))
}

export function useHydratedHead<T extends SchemaAugmentations>(
  input: UseHeadInput<T>,
  options?: UseHeadOptions
): ActiveHeadEntry<UseHeadInput<T>> | void {
  if (input && typeof input === 'object' && !('value' in input)) {
    const title = 'title' in input ? input.title : undefined
    if (title) {
      ;(input as any).title = () => (isHydrated.value ? (typeof title === 'function' ? title() : title) : '')
    }
  }
  return useHead(
    (() => {
      if (!isHydrated.value) return {}
      return resolveUnref(input)
    }) as UseHeadInput<T>,
    options
  )
}
